import type { ObjectDirective } from "vue";

interface LazyloadInstance {
   emitLoaded?: () => void;
}

export const lazyload: ObjectDirective<HTMLElement, boolean | undefined> = {
   getSSRProps() {
      return {};
   },
   mounted(el, binding) {
      const options: IntersectionObserverInit = {
         root: null,
         threshold: 0,
         rootMargin: "0px",
      };
      const handleIntersect: IntersectionObserverCallback = (
         entries,
         observer,
      ) => {
         entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
               const target = entry.target as HTMLElement;
               const loaded = () => {
                  if (!target) {
                     throw Error("Missing Lazyload Element");
                  }
                  target.classList.remove("loading");
                  target.classList.remove("lazy");
                  target.classList.add("loaded");

                  // remove data-attr
                  if (target.localName === "video") {
                     Array.from(target.children).forEach((child) => {
                        child.removeAttribute("data-src");
                     });
                  } else {
                     target.removeAttribute("data-src");
                  }

                  const instance = binding.instance as
                     | LazyloadInstance
                     | null;
                  if (instance?.emitLoaded) {
                     instance.emitLoaded();
                  }
               };
               // set loading class
               target.classList.add("loading");
               // Load listeners
               if (target.localName === "img") {
                  target.addEventListener("load", loaded);
               } else if (target.localName === "video") {
                  target.addEventListener("loadedmetadata", loaded);
               }

               // set srcset
               if (target.dataset.srcset) {
                  target.setAttribute("srcset", target.dataset.srcset);
               }
               if (target.dataset.src) {
                  target.setAttribute("src", target.dataset.src);
               }
               // video
               if (target.localName === "video") {
                  Array.from(target.children).forEach((child) => {
                     const src = (child as HTMLElement).dataset.src;
                     if (src) child.setAttribute("src", src);
                  });
                  (target as HTMLVideoElement).load();
               }
               observer.unobserve(target);
            } else {
               (entry.target as HTMLElement).classList.remove("active");
            }
         });
      };

      if (binding.value === false) {
         return;
      }
      const observer = new IntersectionObserver(handleIntersect, options);
      observer.observe(el);
   },

   beforeMount(el) {
      el.classList.add("lazy");
   },
};
